import React, { useState } from 'react';
import {
	SypacButton,
	SypacIcon,
	SypacText,
} from '@sypac/component-library-react';
import { T, useTranslate } from '@tolgee/react';
import { TrackingService } from '../../services/tracking.service';
import { toastVariant } from '../ToastVariant/toastVariant';
import { PUBLIC_ROUTE_PREFIX } from '../../router/Router/public.router';
import { cn } from '../../lib/utils';
import { copyToClipboard } from '../../utils/clipboard.util';

interface ShareTrackingButtonProps {
	orderId: number;
	iconOnly?: boolean;
}

const ShareTrackingButton: React.FC<ShareTrackingButtonProps> = ({
	orderId,
	iconOnly = false,
}) => {
	const { t } = useTranslate();
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const shareLink = async () => {
		if (isLoading) return;
		setIsLoading(true);
		let url: string;
		try {
			const { data } = await TrackingService.createShareToken(orderId);
			const baseUrl = (process.env.REACT_APP_SHARE_URL || '').replace(
				/\/+$/,
				'',
			);
			url = `${baseUrl}/${PUBLIC_ROUTE_PREFIX}/${encodeURIComponent(
				data.token,
			)}`;
		} catch (e) {
			toastVariant(
				t(
					'shareTracking.linkError',
					'Could not create the tracking link, please try again later',
				),
				true,
			);
			setIsLoading(false);
			return;
		}

		try {
			// The link exists at this point; a refused clipboard must not be
			// reported as a failure to create it — show it for a manual copy.
			if (await copyToClipboard(url)) {
				toastVariant(
					t('shareTracking.linkCopied', 'Tracking link copied to clipboard'),
				);
			} else {
				toastVariant(
					`${t(
						'shareTracking.linkCreatedCopyManually',
						'Tracking link created, copy it from here:',
					)} ${url}`,
					false,
					undefined,
					{
						autoClose: false,
						closeButton: true,
						className: 'max-w-[90vw] break-all select-text z-[2000]',
					},
				);
			}
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<SypacButton variant="secondary" size="small" className="flex-none">
			<button
				type="button"
				onClick={shareLink}
				disabled={isLoading}
				title={iconOnly ? t('shareTracking.share', 'Share') : undefined}
				aria-label={iconOnly ? t('shareTracking.share', 'Share') : undefined}
				className={cn(
					'h-[40px] rounded-lg flex gap-2 items-center border-none bg-alabaster text-gray-80 transition hover:bg-gray-10',
					iconOnly ? 'w-[40px] px-0 justify-center' : 'px-3',
					isLoading ? 'cursor-wait opacity-60' : 'cursor-pointer',
				)}
			>
				<SypacIcon
					iconName="Share"
					size="custom"
					width="22px"
					height="22px"
					className="mb-[2px]"
				/>
				{!iconOnly ? (
					<SypacText variant="body-regular-medium">
						<p>
							<T keyName="shareTracking.share">Share</T>
						</p>
					</SypacText>
				) : null}
			</button>
		</SypacButton>
	);
};

export default ShareTrackingButton;
